import classNames from 'classnames';
import { FunctionComponent } from 'react';

import { useBundle } from '@amzn/react-arb-tools';
import { useWeblab } from '@amzn/sustainability-central-shared-react';

import { MenuItem } from 'src/components/common/menu-item/MenuItem';
import { useFooterNavItems } from 'src/components/footer/hooks/use-footer-nav-items';
import { ASX_REBRANDING } from 'src/constants';
import { NAV_MENU_IDS, NAV_PLACEMENT } from 'src/enums/navigation';
import { RESOURCE_BUNDLES } from 'src/enums/resource-bundles';
import { THEME } from 'src/enums/theme';
import { tf } from 'src/helpers';
import { DEFAULT_THEME, useThemeConfig } from 'src/hooks/use-theme-config';

import styles from './Footer.module.scss';

/**
 * Items in the menu are flex positioned and right align is applied using "margin-left: auto".
 * The behavior is that every item starting with the one with the right align class
 * are shifted to the right. Keep in mind for NRS menu item ordering.
 */
const RIGHT_ALIGN_STARTING = NAV_MENU_IDS.COOKIE_POLICY;

export const Footer: FunctionComponent = () => {
  const { navItems } = useFooterNavItems();
  const { themeConfig } = useThemeConfig();
  const asxRebrandingWeblab = useWeblab(ASX_REBRANDING);

  const isRebrandingApplied =
    asxRebrandingWeblab === 'T1' ||
    themeConfig.value === THEME.REBRAND_DARK ||
    themeConfig.value === THEME.REBRAND_LIGHT;
  const [bundle] = useBundle(RESOURCE_BUNDLES.FOOTER);

  // eslint-disable-next-line no-nested-ternary
  const themeValue = isRebrandingApplied
    ? THEME.REBRAND_LIGHT
    : themeConfig.weblab
      ? DEFAULT_THEME.value
      : themeConfig.value;

  return (
    <footer className={classNames(styles.footer, styles[themeValue])}>
      <nav aria-label={tf(bundle, 'secondary_label', 'Secondary navigation')} className={styles.navigation}>
        <ul className={styles.listContainer}>
          {navItems.map((item) => (
            <MenuItem
              isAlignedRight={item.id === RIGHT_ALIGN_STARTING}
              key={item.id}
              navItem={item}
              placement={NAV_PLACEMENT.FOOTER}
              theme={themeValue}
              resourceBundle={RESOURCE_BUNDLES.FOOTER}
            />
          ))}
        </ul>
      </nav>
    </footer>
  );
};
